<template>
  <div class="content container">
    <div v-if="showPageSizeSelector" class="vehicle-list-toolbar">
      <label class="vehicle-list-page-size-label" for="vehicle-list-page-size">
        {{ t('vehicles-per-page') }}
      </label>
      <select
          id="vehicle-list-page-size"
          class="vehicle-list-page-size-select"
          :value="pageSize"
          @change="onPageSizeChange"
      >
        <option
            v-for="option in pageSizeOptions"
            :key="option"
            :value="option"
        >
          {{ option }}
        </option>
      </select>
    </div>

    <div v-for="vehicle in vehicles" :key="vehicle.id" class="vehicles-list">
      <div
          class="row vehicle-panel"
          :class="[
          { violation: hasViolation(vehicle) },
          { 'no-right-panel': isSG }
        ]"
          @contextmenu.prevent="onRightClick(vehicle)"
      >
        <!-- IMAGE -->
        <div class="image">
          <VehicleImage
              :vehicle="vehicle"
              :image-service="vehService"
              source="vehicle-list"
          />
        </div>

        <!-- LEFT PANEL -->
        <div
            class="measurement-row"
            :class="{ violation: hasViolation(vehicle) }"
            @click="handleGoToDetails(vehicle)"
        >
          <!-- HEADER -->
          <div class="row align-items-center">
            <div class="col-4 measurement-row-header">
              {{ vehicle.location.shortName }}
            </div>

            <div class="col-4 plate-item">
              <div class="row measurement-row-header">
                <div
                    class="col-2 plate-flag center-aligned"
                    :style="{ backgroundColor: getCode(vehicle.nationality) ? '' : 'white' }"
                >
                  <country-flag :country="getCode(vehicle.nationality)" size="small" />
                  <div class="country-code">
                    {{ getCode(vehicle.nationality) || '' }}
                  </div>
                </div>
                <div class="col-8 plate">{{ getPlate(vehicle) }}</div>
              </div>
            </div>

            <div class="col-2 measurement-row-header-thin center-aligned">
              {{ getFormattedDate(vehicle.createdAt) }}
            </div>
            <div class="col-2 measurement-row-header-thin center-aligned">
              {{ getFormattedTime(vehicle.createdAt) }}
            </div>
          </div>

          <!-- TOTAL WEIGHT -->
          <div v-if="shouldShowTotalWeight" class="row measurement-row-row">
            <div class="col-8 measurement-row-field">
              {{ t('total-weight') }} [limit {{ vehicle.limitTotalLoad }} kg]
            </div>
            <div :class="['col-4 field-center', getWeightClassName(vehicle)]">
              {{ vehicle.totalLoad }} kg
            </div>
          </div>

          <!-- LENGTH -->
          <div v-if="shouldShowLength" class="row measurement-row-row">
            <div class="col-8 measurement-row-field">
              {{ t('length') }}
            </div>
            <div class="col-4 field-center">
              {{ vehicle.vehLength }} cm
            </div>
          </div>

          <!-- HEIGHT -->
          <div v-if="shouldShowHeight" class="row measurement-row-row">
            <div class="col-8 measurement-row-field">
              {{ t('height') }}
            </div>
            <div :class="['col-4 field-center', getHeightClassName(vehicle)]">
              <span v-if="vehicle.vehHeight != null">
                {{ vehicle.vehHeight }} cm
              </span>
              <span v-else>
                {{ vehicle.overheight ? 'TAK' : 'NIE' }}
              </span>
            </div>
          </div>

          <!-- SPEED + LIMIT -->
          <div v-if="shouldShowSpeedLimit" class="row measurement-row-row">
            <div class="col-8 measurement-row-field">
              {{ t('speed') }}
            </div>
            <div class="col-4 field-center">
              {{ vehicle.speed }} km/h
            </div>
          </div>

          <!-- NUMBER OF AXLES -->
          <div v-if="shouldShowNumberOfAxles" class="row measurement-row-row">
            <div class="col-8 measurement-row-field">
              {{ t('number-of-axles') }}
            </div>
            <div class="col-4 field-center">
              {{ vehicle.numberOfAxles }}
            </div>
          </div>

          <!-- CATEGORY CTG8P1 -->
          <div v-if="shouldShowCtg8p1" class="row measurement-row-row">
            <div class="col-8 measurement-row-field">
              {{ t('category') }}
            </div>
            <div class="col-4 field-center">
              {{ vehicle.ctg8p1 }}
            </div>
          </div>

          <!-- NATIONALITY -->
          <div v-if="shouldShowNationality" class="row measurement-row-row">
            <div class="col-8 measurement-row-field">
              {{ t('nationality') }}
            </div>
            <div class="col-4 field-center">
              {{ vehicle.nationality || '-' }}
            </div>
          </div>

          <!-- DIRECTION / WRONG DIRECTION -->
          <div v-if="shouldShowDirectionWrongDirection" class="row measurement-row-row">
            <div class="col-8 measurement-row-field">
              {{ t('direction-and-wrong-way') }}
            </div>
            <div class="col-4 field-center">
              {{ formatYesNo(vehicle.direction) }} / {{ formatYesNo(vehicle.wrongDirection) }}
            </div>
          </div>

          <!-- VIOLATIONS -->
          <div v-if="shouldShowViolationsFlags" class="row measurement-row-row">
            <div class="col-8 measurement-row-field">
              {{ t('violations-flags') }}
            </div>
            <div :class="['col-4 field-center', hasAnyViolationFlag(vehicle) ? 'measurement-row-field-b-red' : 'measurement-row-field-b']">
              <ViolationFlags :vehicle="vehicle" />
            </div>
          </div>

          <!-- MAKE (marka) -->
          <div v-if="shouldShowMake" class="row measurement-row-row">
            <div class="col-8 measurement-row-field">
              {{ t('vehicle-make') }}
            </div>
            <div class="col-4 field-center">
              {{ vehicle.mmrMake || '-' }}
            </div>
          </div>

          <!-- MODEL -->
          <div v-if="shouldShowModel" class="row measurement-row-row">
            <div class="col-8 measurement-row-field">
              {{ t('vehicle-model') }}
            </div>
            <div class="col-4 field-center">
              {{ vehicle.mmrModel || '-' }}
            </div>
          </div>
        </div>

        <!-- RIGHT PANEL -->
        <div
            v-if="!isSG"
            class="load"
            :class="{ violation: hasViolation(vehicle) }"
            @click="handleGoToDetails(vehicle)"
        >
          <div class="row measurement-row-header">
            <div
                class="items-centered center-aligned"
                style="display: flex; align-items: center; gap: 50px; margin-left: 5px;"
            >
              <CountdownTimer
                  :deadline="moment(vehicle.checkpointTime).format('YYYY/MM/DD HH:mm:ss')"
                  :server-diff="momentDiffServer"
                  :obj-number="String(vehicle.id)"
              />
              <div style="display: flex; align-items: center; gap: 8px;">
                <span>{{ t('axle-load').toUpperCase() }} [t]</span>
                <span
                    v-if="selectedVehicleIds.includes(vehicle.id)"
                    class="checkmark"
                >
                  ✔
                </span>
              </div>
            </div>
          </div>

          <div class="row measurement-row-body centered-flex" v-if="existsAxlesRow1(vehicle.axles)">
            <div class="col-1 axle-1">
              <div class="row-cols-3 axle-field">{{ t('axle') }}:</div>
              <div class="row-cols-6">
                <div class="icon-a"></div>
              </div>
              <div class="row-cols-3 axle-field">t:</div>
            </div>
            <div
                v-for="ax in getAxlesRow1(vehicle.axlesComplex.axlesGroups)"
                :key="ax"
                :class="['col-1', 'width width-' + ax.type]"
                v-html="getDivs(ax)"
            ></div>
          </div>

          <div v-if="existsAxlesRow2(vehicle.axles)" class="row" id="axles8to14">
            <div class="col-1">
              <div class="row-cols-3 axle-field">{{ t('axle') }}:</div>
              <div class="row-cols-6">
                <div class="icon-a"></div>
              </div>
              <div class="row-cols-3 axle-field">t:</div>
            </div>
            <div v-for="ax in getAxlesRow2(vehicle.axles)" :key="ax" class="col-1">
              <div class="items-centered-axles center-aligned">
                <div :class="'row-cols-3 ' + getAxisNumberClass(ax)">
                  {{ ax.type }}
                </div>
                <div class="row-cols-6">
                  <div :class="'icon-a ' + getAxisClass(ax)"></div>
                </div>
                <div :class="'row-cols-3 ' + getAxisNumberClass(ax)">
                  {{ ax.totalLoad }}
                </div>
              </div>
            </div>
          </div>
        </div>

      </div>
    </div>
  </div>
</template>

<script>
import moment from 'moment'
import CountryFlag from 'vue-country-flag-next'
import getCountryISO2 from 'country-iso-3-to-2'
import VehicleImage from '@/components/VehicleImage'
import CountdownTimer from '@/components/current/countdown-timer'
import ViolationFlags from '@/components/ViolationFlags'
import ArchivalMeasurementsService from '@/services/ArchivalMeasurementsService'
import { useUserStore } from '@/stores/user.store'
import { isSG } from '@/config/appVersion'

moment.locale('pl')

export default {
  name: 'VehicleList',

  components: {
    VehicleImage,
    CountdownTimer,
    CountryFlag,
    ViolationFlags,
  },

  props: {
    vehicleList: { required: true },
    dataService: { required: true },
    detailsPath: { default: null },
    goToDetailsView: { type: Function },
    isFromTags: { type: Boolean, default: false },
    showPageSizeSelector: { type: Boolean, default: false },
    pageSize: { type: Number, default: 10 },
    pageSizeOptions: {
      type: Array,
      default: () => [10, 25, 50],
    },
  },
  emits: ['openDetails', 'page-size-change'],

  data() {
    return {
      vehService: this.dataService,
      vehicles: [],
      selectedVehicleIds: [],
      vehicleTags: {},
      taggingInProgress: new Set(),
      userStore: useUserStore(),
      momentDiffServer: 0,
      isSG,
      moment,
    }
  },

  computed: {
    selectedMetricIds() {
      const ids = this.userStore?.preferences?.vehicleListMetricIds
      if (!Array.isArray(ids)) return null

      return ids
        .map((id) => Number(id))
        .filter((id) => Number.isInteger(id) && id > 0)
    },
    metricsVisibilityPref() {
      return this.userStore?.preferences?.vehicleListMetricsVisibility || null
    },
    shouldShowTotalWeight() {
      const ids = this.selectedMetricIds
      if (ids) return ids.includes(1)
      const pref = this.metricsVisibilityPref
      if (!pref) return true
      return pref.totalWeight !== false
    },
    shouldShowLength() {
      const ids = this.selectedMetricIds
      if (ids) return ids.includes(2)
      const pref = this.metricsVisibilityPref
      if (!pref) return true
      return pref.length !== false
    },
    shouldShowHeight() {
      const ids = this.selectedMetricIds
      if (ids) return ids.includes(3)
      const pref = this.metricsVisibilityPref
      if (!pref) return true
      return pref.height !== false
    },
    shouldShowSpeedLimit() {
      const ids = this.selectedMetricIds
      return !!ids && ids.includes(4)
    },
    shouldShowNumberOfAxles() {
      const ids = this.selectedMetricIds
      return !!ids && ids.includes(5)
    },
    shouldShowCtg8p1() {
      const ids = this.selectedMetricIds
      return !!ids && ids.includes(6)
    },
    shouldShowNationality() {
      const ids = this.selectedMetricIds
      return !!ids && ids.includes(7)
    },
    shouldShowDirectionWrongDirection() {
      const ids = this.selectedMetricIds
      return !!ids && ids.includes(8)
    },
    shouldShowViolationsFlags() {
      const ids = this.selectedMetricIds
      return !!ids && ids.includes(9)
    },
    shouldShowMake() {
      const ids = this.selectedMetricIds
      return !!ids && ids.includes(10)
    },
    shouldShowModel() {
      const ids = this.selectedMetricIds
      return !!ids && ids.includes(11)
    },
  },

  watch: {
    vehicleList: {
      immediate: true,
      handler(newVal = []) {
        this.vehicles = newVal

        const validIds = new Set(newVal.map(v => Number(v.id)))

        this.selectedVehicleIds = this.selectedVehicleIds.filter(id =>
            validIds.has(id)
        )

        Object.keys(this.vehicleTags).forEach(id => {
          if (!validIds.has(Number(id))) delete this.vehicleTags[id]
        })

        if (this.isFromTags) {
          this.selectedVehicleIds = newVal.map(v => Number(v.id))
        }
      },
    },
  },

  async mounted() {
    await this.userStore.self()
    this.momentDiffServer =
        Number(this.userStore?.preferences?.checkpointAdditionalTime || 0) * 1000
  },

  methods: {
    /* ================= DETAILS ================= */

    handleGoToDetails(vehicle) {
      this.$emit('openDetails', vehicle)

      if (this.goToDetailsView) {
        this.goToDetailsView(vehicle)
        return
      }

      if (!this.detailsPath) return

      const path = this.detailsPath.includes('vehicleArch')
          ? `/${this.detailsPath}/${vehicle.id}/${vehicle.location.id}/${this.getCreatedAt(vehicle)}`
          : `/${this.detailsPath}/${vehicle.id}/${vehicle.location.id}`

      this.$router.push({
        path,
        query: {
          backTo: this.$route.fullPath,
        },
      })
    },

    /* ================= TAGGING ================= */

    async onRightClick(vehicle) {
      const id = Number(vehicle.id)

      if (this.taggingInProgress?.has(id)) return
      this.taggingInProgress?.add(id)

      try {
        if (this.selectedVehicleIds.includes(id)) {
          await this.removeTagFlow(vehicle)
        } else {
          await this.addTagFlow(vehicle)
        }
      } catch (e) {
        // ⬇⬇⬇ KLUCZOWE ⬇⬇⬇
        console.error('Tag operation failed:', e)
        // NIC WIĘCEJ – NIE RZUCAMY
      } finally {
        this.taggingInProgress?.delete(id)
      }
    },

    existsAxlesRow1(axles) {
      return axles;
    },
    getAxlesRow1(axles) {
      if (axles) {
        return axles;
      }
    },

    async addTagFlow(vehicle) {
      try {
        const id = Number(vehicle.id)

        const res = await ArchivalMeasurementsService.addTag(
            id,
            vehicle.location.id,
            this.getCreatedAt(vehicle)
        )

        const stillExists = this.vehicles.some(v => Number(v.id) === id)
        if (!stillExists) return

        if (res?.id) {
          this.vehicleTags[id] = res.id
          this.selectedVehicleIds.push(id)
        }
      } catch (e) {
        console.error('addTag failed:', e)
        // ⛔ NIE RZUCAMY
      }
    },

    getWidthValue(ax) {
      if (ax.type == 4) {
        return `mr-8`;
      }
      return "";
    },

    existsAxlesRow2(axles) {
      return axles && axles.size > 7;
    },

    getAxisNumberClass(axis) {
      if (axis && axis.overload) {
        return "axle-field-red";
      }
      return "axle-field";
    },

    async removeTagFlow(vehicle) {
      const id = Number(vehicle.id)
      const tagId = this.vehicleTags[id]
      if (!tagId) return

      await ArchivalMeasurementsService.removeTag(
          id,
          tagId,
          vehicle.location.id,
          this.getCreatedAt(vehicle)
      )

      delete this.vehicleTags[id]
      this.selectedVehicleIds = this.selectedVehicleIds.filter(v => v !== id)
    },

    /* ================= HELPERS ================= */

    hasViolation(vehicle) {
      const flags = [
        'overloadSA',
        'overloadMA',
        'overloadTL',
        'overheight',
        'overlane',
        'overload',
        'overspeed',
        'wrongdirection',
      ]
      return flags.some(f => vehicle[f])
    },

    getAxisClass(axis) {
      if (!axis || !axis.type) return "";

      const baseClass = "axis-icon";
      const typeSuffix = axis.type > 1 ? `-${axis.type}` : "";
      const overloadSuffix = axis.overload ? "-ov" : "";

      const iconClass = "icon-a";
      const typeIconSuffix = axis.type > 1 ? `-${axis.type}` : "";
      const overloadIconSuffix = axis.overload ? "-ov" : "";

      return `${iconClass}${typeIconSuffix}${overloadIconSuffix} ${baseClass}${overloadSuffix}${typeSuffix}`;
    },

    getDivs(ax) {
      let divs = "";

      if (ax.type == 2 || ax.type == 3) {
        let totalLoad = (ax.totalLoad / 1000).toFixed(2);
        divs += `
          <div class="d-flex flex-column">
            <div class="row-cols-3 ${this.getAxisNumberClass(ax)} center-aligned">
              ${ax.type}
            </div>
            <div class="row-cols-6 center-aligned">
              <div class="${this.getAxisClass(ax)}"></div>
            </div>
            <div class="row-cols-3 ${this.getAxisNumberClass(ax)} center-aligned">
              ${totalLoad}
            </div>
          </div>`;
      } else {
        divs += `<div class="d-flex flex-rows">`;
        ax.axles.forEach((singleAxle) => {
          let singleTotalLoad = (singleAxle.totalLoad / 1000).toFixed(2);

          divs += `
            <div class="d-flex flex-column align-items-center ${this.getWidthValue(
              singleAxle
          )}" style="margin-right: 8px;">
              <div class="row-cols-3 ${this.getAxisNumberClass(
              singleAxle
          )} center-aligned">
                ${singleAxle.type > 3 ? "W" : singleAxle.type}
              </div>
              <div class="row-cols-6 center-aligned">
                <div class="${this.getAxisClass(singleAxle)}"></div>
              </div>
              <div class="row-cols-3 ${this.getAxisNumberClass(
              singleAxle
          )} center-aligned">
                ${singleTotalLoad}
              </div>
            </div>`;
        });
        divs += `</div>`;
      }

      return divs;
    },

    getWeightClassName(v) {
      return v.totalLoad > v.limitTotalLoad
          ? 'measurement-row-field-b-red'
          : 'measurement-row-field-b'
    },

    getHeightClassName(v) {
      return v.overheight
          ? 'measurement-row-field-b-red'
          : 'measurement-row-field-b'
    },

    getCode(v) {
      return getCountryISO2(v) || ''
    },

    getPlate(v) {
      return v.plateLpr || '-----'
    },

    getCreatedAt(v) {
      return moment(v.createdAt).toISOString(false)
    },

    // Czas pomiaru wyświetlamy dokładnie tak, jak przyszedł z API.
    // checkpointAdditionalTime (momentDiffServer) wydłuża tylko widoczność pojazdu
    // na liście i odliczanie do punktu kontroli - nie wolno go doliczać do createdAt.
    getFormattedDate(d) {
      return moment(d).format('DD.MM.YYYY')
    },

    getFormattedTime(d) {
      return moment(d).format('HH:mm:ss')
    },

    formatYesNo(value) {
      return value ? this.$t('yes') : this.$t('no')
    },

    hasOverload(v) {
      return Boolean(
          v?.overload ||
          Number(v?.overloadSA || 0) > 0 ||
          Number(v?.overloadMA || 0) > 0 ||
          Number(v?.overloadTL || 0) > 0
      )
    },

    hasAnyViolationFlag(v) {
      return Boolean(
          this.hasOverload(v) ||
          v?.overspeed ||
          v?.overheight ||
          v?.overlane ||
          v?.wrongDirection
      )
    },

    onPageSizeChange(event) {
      const nextPageSize = Number(event.target.value)
      const allowedOptions = this.pageSizeOptions.map(option => Number(option))

      if (!allowedOptions.includes(nextPageSize)) return

      this.$emit('page-size-change', nextPageSize)
    },
  },
}
</script>

<style scoped>
.vehicle-list-toolbar {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: var(--spacing-md);
  padding: var(--spacing-md) 0;
  font-size: var(--font-size-sm);
}

.vehicle-list-page-size-label {
  color: var(--text-secondary);
  font-weight: var(--font-weight-medium);
}

.vehicle-list-page-size-select {
  min-width: 80px;
  min-height: var(--control-height-sm);
  padding: 0 var(--spacing-sm);
  border: 1px solid var(--border-default);
  border-radius: var(--control-radius);
  background-color: var(--surface-primary);
  color: var(--text-primary);
  margin-right: 0.7rem;
}

.checkmark {
  color: var(--status-ok);
  font-size: var(--font-size-xl);
  font-weight: var(--font-weight-bold);
  margin-left: 60px;
}

/* O wysokości karty decyduje miniatura (`.image` → `img-fluid`, proporcje
   zdjęcia), a oba panele są rozciągane do jej wysokości. Nadwyżkę muszą
   rozdzielić między siebie wiersze — inaczej cała zostawała pustym pasem pod
   ostatnim wierszem i „Wysokość" wyglądała na wyższą od pozostałych wierszy. */
.measurement-row {
  display: flex;
  flex-direction: column;
  border-right: 1px solid var(--border-default);
  background: var(--surface-tertiary);
  width: 50%;
  height: auto;
}

/* Wiersze parametrów dzielą nadwyżkę wysokości po równo (`flex-basis: 0`),
   więc każdy ma dokładnie tyle samo miejsca. Nagłówek nie rośnie — zostaje na
   `--measurement-row-height`, wspólnej z nagłówkiem prawego panelu. */
.measurement-row > .measurement-row-row {
  flex: 1 1 0;
}

.measurement-row, .load {
  position: relative;
}

/* Wszystkie wiersze karty (nagłówek, komórki nagłówka, wiersze parametrów)
   trzymają tę samą wysokość i centrują treść w pionie — żadnych dopychaczy
   `padding-top`. Dotyczy też nagłówka prawego panelu, który jest kontenerem
   `.row`, więc jego treść centruje się tak samo jak w lewym. */
.measurement-row-header,
.measurement-row-header-thin,
.measurement-row-row {
  display: flex;
  align-items: center;
  min-height: var(--measurement-row-height);
}

.measurement-row-header {
  font-weight: var(--font-weight-bold);
  font-size: var(--font-size-lg);
  background-color: var(--surface-primary);
  color: var(--text-primary);
}

/* Poziomy padding tylko wtedy, gdy klasa opisuje KOMÓRKĘ. Ta sama klasa służy
   też jako kontener `.row` (tablica rejestracyjna, nagłówek prawego panelu) —
   tam padding rozepchnąłby kolumny. */
.measurement-row-header:not(.row) {
  padding: 0 var(--measurement-cell-pad-x);
}

.measurement-row-header-thin {
  justify-content: center;
  font-weight: var(--font-weight-regular);
  font-size: var(--font-size-md);
  background-color: var(--surface-primary);
  border-left: 1px solid var(--border-subtle);
  color: var(--text-primary);
}

.measurement-row-row {
  font-size: var(--font-size-sm);
  border-bottom: 1px solid var(--border-subtle);
}

.measurement-row-row .field-center {
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: var(--font-weight-bold);
  font-size: var(--font-size-md);
}

/* Komórki: jeden poziomy padding dla etykiety, wartości i pól nagłówka.
   Selektor z rodzicem daje wyższą specyficzność niż reset `.row > *`
   (padding: 0) niżej w tym arkuszu — bez sięgania po `!important`. */
.measurement-row .measurement-row-field,
.measurement-row .field-center,
.measurement-row .measurement-row-header-thin,
.plate-item .plate {
  padding: 0 var(--measurement-cell-pad-x);
}

.measurement-row-body {
  min-height: 118px;
  background: var(--surface-tertiary);
  overflow-x: auto;
  white-space: nowrap;
}


.measurement-row-field-b {
  font-weight: var(--font-weight-bold);
  font-size: var(--font-size-md);
}

.measurement-row-field-b-red {
  font-weight: var(--font-weight-bold);
  font-size: var(--font-size-md);
  color: var(--status-error);
}

.width {
  align-items: center;
  margin-right: 8px !important;
}

.width-1 {
  width: 30px;
}
.width-2 {
  width: 60px;
}
.width-3 {
  width: 90px;
}
.width-4 {
  width: 140px;
}

.mr-8 {
  margin-right: 8px;
}

.field-icon {
  background-position-x: 80px;
  text-indent: -25px;
}

.vehicle-panel {
  width: 100%;
  margin-left: 0;
  position: relative; /* potrzebne dla overlayu */

  /* JEDNA metryka dla wszystkich wierszy karty pomiaru — nagłówka i wierszy
     parametrów, w lewym i w prawym panelu. Wcześniej każdy rodzaj wiersza miał
     własną wysokość (35 px nagłówek, ~33 px wiersz parametru) i własny padding
     dopychany od góry (5 / 7 / 10 px, część z `!important`), więc etykiety
     i wartości nie leżały na jednej osi.

     36 px pomieści tablicę rejestracyjną (`--font-size-2xl` = 22 px, linia
     ~32 px) bez obcinania. `min-height`, nie `height` — dłuższe tłumaczenie
     etykiety może zawinąć wiersz, zamiast wyjść poza jego obszar. */
  --measurement-row-height: 36px;
  --measurement-cell-pad-x: var(--spacing-sm);
}

.vehicle-panel .image {
  padding: 0;
  width: 20%;
  overflow: hidden;
  align-self: stretch;
  display: flex;
  align-items: center;
  justify-content: center;
}

.load {
  display: flex;
  flex-direction: column;
  width: 30%;
}

/* Blok osi zajmuje całą wysokość pod nagłówkiem i centruje zawartość
   (`.centered-flex`), zamiast zostawiać pusty pas pod sobą. */
.load > .measurement-row-body {
  flex: 1 1 auto;
}

.centered-flex {
  display: flex;
  justify-content: center;
  align-items: center;
}

/* Flaga i kod kraju jeden pod drugim — dłuższe tłumaczenia nagłówków (EN/UA)
   zwężają tę kolumnę i ustawienie w poziomie wychodziło poza niebieskie pole. */
.plate-flag {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 2px;
  line-height: 1;
  width: 45px;
  height: var(--measurement-row-height);
  background: var(--accent-blue);
  color: var(--text-inverse);
  font-weight: var(--font-weight-bold);
  font-size: var(--font-size-sm);
}

/* Nadpisuje margines flagi z vue-country-flag-next (domyślnie -0.9em -1.2em -0.9em -1.2em).
   Sprite ma 52 × 39 px i jest skalowany do 25 % (`transform: scale(.25)`), więc bez korekty
   zajmowałby w układzie czterokrotnie więcej miejsca, niż widać. Marginesy przycinają pudełko
   do faktycznych 13 × 9.75 px (przy `--font-size-sm` = 13 px), dzięki czemu kod kraju siada
   dokładnie pod flagą. Selektor z .plate-flag daje wyższą specyficzność niż reguła
   .small-flag[data-v-...] z biblioteki, więc nie zależy od kolejności stylów. */
.plate-flag .small-flag {
  margin: -1.1em -1.5em;
}

.plate-flag .country-code {
  text-align: center;
}

/* Tablica rejestracyjna — najważniejsza informacja w karcie pomiaru. */
.plate {
  display: flex;
  align-items: center;
  background: var(--surface-primary);
  color: var(--text-primary);
  font-weight: var(--font-weight-bold);
  font-size: var(--font-size-2xl);
  min-height: var(--measurement-row-height);
}

.plate-item {
  min-height: var(--measurement-row-height);
}

.axle-row .col-1 {
  padding: 0 15px;
}

.axle-row .col-1:first-child {
  padding: 0;
}

.axle-field {
  font-size: var(--font-size-sm);
  background: var(--surface-tertiary);
}

.axle-field-red {
  font-weight: var(--font-weight-bold);
  font-size: var(--font-size-sm);
  background: var(--surface-tertiary);
  color: var(--status-error);
}

.count2,
.count3,
.count4,
.count5 {
  margin-top: 25px;
}

.count6,
.count7,
.count8,
.count9,
.count10 {
  margin-top: 25px;
}

.with-margin-8 {
  margin-right: 8px;
}

.items-centered-axles {
  width: 30px;
}

/* Każdy pomiar to osobna karta — czytelniejsze niż jedna długa tabela. */
.vehicles-list {
  width: 100%;
  margin: 0 0 var(--spacing-md);
  background: var(--surface-primary);
  border: 1px solid var(--border-default);
  border-radius: var(--panel-radius);
  box-shadow: var(--shadow-panel);
  overflow: hidden;
}

.measurement-row-row:last-child {
  border-bottom: none;
}

.row {
  margin: 0;
  padding: 0;
  display: flex;
}

.row > * {
  margin: 0;
  padding: 0;
}


.image-icon {
  height: 20px;
}

.measurement-row.violation::after, .load.violation::after {
  content: "";
  position: absolute;
  inset: 0;
  background: rgba(255, 80, 80, 0.18);
  pointer-events: none;
  z-index: 5;
}

/* gdy prawy panel ukryty (isSG=true), lewy ma zająć miejsce prawego */
.vehicle-panel.no-right-panel .measurement-row {
  width: 80%;
}

/* prawy i tak znika przez v-if, ale zostawiam dla pewności jeśli kiedyś zmienisz na v-show */
.vehicle-panel.no-right-panel .load {
  display: none;
}
</style>
